import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Paper,
} from "@mui/material";
import { Briefcase, Shield, TrendingUp } from "lucide-react";
import WrapperComponent from "../WrapperComponent";
import BackButton from "../../../components/buttons/BackButton";
import NextButton from "../../../components/buttons/NextButton";
import { useDpDisplayOverride } from "../../../hooks/useDpDisplayOverride";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
});

const formatCurrency = (amount) => currencyFormatter.format(amount || 0);

const DetailRow = ({ label, value, valueColor = "#000" }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1.8,
        px: 0,
        borderBottom: `1px solid #e8eef5`,
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        sx={{
          color: "#1e40af",
          fontWeight: 500,
          fontSize: "15px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: valueColor,
          fontWeight: 600,
          fontSize: "16px",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
};

const ProductCard = ({ data }) => {
  const { invoiceValue } = useDpDisplayOverride(data);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: "16px",
        border: "1px solid #e8eef5",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        width: "100%",
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
          color: "white",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        <Briefcase size={22} />
        Product Details
      </Box>

      <CardContent sx={{ padding: "24px" }}>
        <DetailRow
          label="Make:"
          value={data?.batteryType?.split(" ")[1]?.trim() || "-"}
        />
        <DetailRow
          label="Model:"
          value={data?.batteryType?.split(" ")[0]?.trim() || "-"}
        />
        <DetailRow label="Variant:" value={data?.variant || "Lithium"} />
        <DetailRow
          label="Ex-Showroom:"
          value={formatCurrency(invoiceValue)}
          valueColor="#059669"
        />
      </CardContent>
    </Card>
  );
};

const LoanDetailsCard = ({ data }) => {
  const { loanAmount, downPayment } = useDpDisplayOverride(data);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: "16px",
        border: "1px solid #e8eef5",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          color: "white",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: "24px" }}>₹</span>
        Loan Details
      </Box>

      {/* Content */}
      <CardContent sx={{ padding: "24px" }}>
        <DetailRow
          label="Proposed Loan:"
          value={formatCurrency(loanAmount)}
          valueColor="#059669"
        />
        <DetailRow label="Tenure:" value={`${data?.tenure} Months`} />
        <DetailRow
          label="EMI (Approx):"
          value={`${formatCurrency(data?.emi?.monthly)}/month`}
          valueColor="#dc2626"
        />
        <DetailRow label="Margin Money:" value={formatCurrency(downPayment)} />
        <DetailRow
          label="Processing Fee:"
          value={formatCurrency(
            (data?.processingFee || 0) + (data?.gstOnProcessingFee || 0),
          )}
        />
      </CardContent>
    </Card>
  );
};

const LoanShieldCard = ({ data }) => {
  const hasShield = data?.loanSuraksha > 0;
  const bgGradient = hasShield
    ? "linear-gradient(135deg, #f0fdf4 0%, #f9fefb 100%)"
    : "linear-gradient(135deg, #fef2f2 0%, #fefbfb 100%)";
  const shieldColor = hasShield ? "#059669" : "#dc2626";

  return (
    <Paper
      elevation={2}
      sx={{
        background: bgGradient,
        borderRadius: "16px",
        // border: `2px solid ${borderColor}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        p: 3,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2 }}>
        <Shield size={28} color={shieldColor} fill={shieldColor} />
        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Loan Shield Taken:
          </Typography>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: shieldColor,
            }}
          >
            {hasShield ? "Yes" : "No"}
          </Typography>
        </Box>
      </Box>

      {hasShield && (
        <>
          <Box
            sx={{ height: "1px", background: "#e2e8f0", margin: "12px 0" }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#64748b",
                fontWeight: 500,
              }}
            >
              Loan Shield Amount:
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#059669",
              }}
            >
              {formatCurrency(data?.loanSuraksha)}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

const EstimatedEMICard = ({ data }) => {
  const { emi } = useDpDisplayOverride(data);

  return (
    <Paper
      elevation={2}
      sx={{
        background: "linear-gradient(135deg, #f0fdf4 0%, #f9fefb 100%)",
        borderRadius: "16px",
        // border: "2px solid #86efac",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        p: 3,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "center", color: "#059669" }}>
          <TrendingUp size={28} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Estimated EMI:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#059669",
              }}
            >
              {formatCurrency(emi?.monthly)}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#64748b",
                marginTop: "2px",
              }}
            >
              /month
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const LoanApplicationDetails = ({ loanData = {}, setActiveStep }) => {
  return (
    <WrapperComponent
      title="Loan Application Details"
      subTitle="Complete loan and product information"
      icon={<Briefcase size={40} color="white" />}
      actions={[
        <BackButton
          onClick={() => {
            setActiveStep((prev) => prev - 1);
          }}
        />,
        <NextButton
          onClick={() => {
            setActiveStep((prev) => prev + 1);
          }}
        />,
      ]}
    >
      <Box>
        <Stack direction={{ xs: "column", md: "row" }} gap={3} mb={3}>
          <ProductCard data={loanData} />
          <LoanDetailsCard data={loanData} />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} gap={3} mb={3}>
          <LoanShieldCard data={loanData} />
          <EstimatedEMICard data={loanData} />
        </Stack>
      </Box>
    </WrapperComponent>
  );
};

export default LoanApplicationDetails;
